import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Save, Loader2 } from 'lucide-react';
import { useNotification } from '../lib/NotificationProvider';

const PartsPopup = ({
  parts = [],
  isOpen,
  onClose,
  anchorRef,
  position,
  poNumber,
  shipment,
  onAddParts,
  onRevertParts,
  isFromDashboard = false,
  onSaveChanges
}) => {
  const popupRef = useRef(null);
  const { markDataChanged } = useNotification();
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPartName, setNewPartName] = useState('');
  const [newPartQuantity, setNewPartQuantity] = useState('');
  const [localParts, setLocalParts] = useState(parts);
  const [originalParts, setOriginalParts] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [partNameError, setPartNameError] = useState(null);

  const handleClose = () => {
    setHasUnsavedChanges(false);
    setError(null);
    setPartNameError(null);
    onClose();
  };

  const handleSaveChanges = async () => {
    if (!onSaveChanges) return;
    setError(null);
    setIsSaving(true);
    try {
      await onSaveChanges(localParts);
      setHasUnsavedChanges(false);
      markDataChanged();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message
        || 'Failed to save parts. Please try again.';
      setError(errorMessage);
      // Revert BOTH local and parent state on failure
      setLocalParts([...originalParts]);
      if (onRevertParts) {
        onRevertParts([...originalParts]);
      }
      setHasUnsavedChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(parts)) {
      setLocalParts(parts);
    }
  }, [parts]);

  // Capture original parts when popup opens for rollback on save failure
  useEffect(() => {
    if (isOpen && Array.isArray(parts)) {
      setOriginalParts([...parts]);
    }
  }, [isOpen, parts]);

  useEffect(() => {
    if (!anchorRef || !position) return;
    if (isOpen && anchorRef.current && popupRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      let top, left;
      switch (position) {
        case 'top':
          top = anchorRect.top - popupRect.height - 8;
          left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;
          break;
        case 'right':
          top = anchorRect.top;
          left = anchorRect.right + 8;
          break;
        case 'left':
          top = anchorRect.top;
          left = anchorRect.left - popupRect.width - 8;
          break;
        default:
          top = anchorRect.bottom + 8;
          left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;
          break;
      }
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      if (left < 10) left = 10;
      if (left + popupRect.width > viewport.width - 10) {
        left = viewport.width - popupRect.width - 10;
      }
      if (top < 10) top = 10;
      if (top + popupRect.height > viewport.height - 10) {
        top = viewport.height - popupRect.height - 10;
      }
      setPopupPosition({ top, left });
    }
  }, [isOpen, position, anchorRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        anchorRef &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, anchorRef]);

  // Check if part number already exists (case-insensitive)
  const checkPartNumberExists = (partNumber) => {
    return localParts.some(
      part => part.part_number?.toLowerCase().trim() === partNumber?.toLowerCase().trim()
    );
  };

  const handleAddPartsClick = () => {
    setShowAddForm(true);
  };

  const handleAddNewPart = () => {
    if (!newPartName.trim() || !newPartQuantity.trim()) return;
    const quantity = parseInt(newPartQuantity, 10);
    if (isNaN(quantity) || quantity <= 0) return;

    // Check for duplicate part number
    setPartNameError(null);
    if (checkPartNumberExists(newPartName)) {
      setPartNameError('A part with this name already exists');
      return;
    }

    const newPart = {
      part_number: newPartName.trim(),
      qty: quantity,
      related_po_number: poNumber,
      related_shipment: shipment,
      _isNew: true  // Mark as new so we know to use createParts API
    };
    setLocalParts((prev) => [...prev, newPart]);
    setHasUnsavedChanges(true);
    setNewPartName('');
    setNewPartQuantity('');
    // Sync to parent immediately so parts tab shows the new part
    // On save failure, onRevertParts will be called to revert parent state
    if (onAddParts) {
      onAddParts(newPart);
    }
  };

  const PopupFooter = () => (
    <div className="px-4 py-3 border-t rounded-b-lg border-gray-100 bg-gray-50">
      <div className="flex justify-between items-center">
        {isFromDashboard && hasUnsavedChanges && (
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className={`flex items-center text-xs font-medium text-white px-3 py-1.5 rounded transition-colors ${
              isSaving ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isSaving ? (
              <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
              <Save size={14} className="mr-1" />
            )}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
        <div className="flex items-center">
          {showAddForm ? (
            <>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewPartName('');
                  setNewPartQuantity('');
                  setPartNameError(null);
                }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 rounded transition-colors mr-2"
              >
                Close
              </button>
              <button
                onClick={handleAddNewPart}
                className="flex items-center text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded transition-colors"
                disabled={!newPartName.trim() || !newPartQuantity.trim()}
              >
                <Plus size={14} className="mr-1" />
                Add
              </button>
            </>
          ) : (
            <button
              onClick={handleAddPartsClick}
              className="flex items-center text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded transition-colors"
            >
              <Plus size={14} className="mr-1" />
              Add Parts
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!isOpen || !localParts) return null;

  // Fallback popup (if anchorRef or position doesn't exist).
  if (!anchorRef || !position) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          ref={popupRef}
          className="bg-white rounded-lg shadow-xl border border-gray-200 w-[600px] max-h-[90vh] flex flex-col transition-all duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex rounded-lg items-center justify-between p-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-medium text-gray-700">
              Parts Info {poNumber && <span className="text-red-600 ml-1">PO #{poNumber}</span>}
            </h3>
            <button
              onClick={handleClose}
              className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-3">
            {error && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {error}
              </div>
            )}
            {Array.isArray(localParts) && localParts.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-12 text-xs font-medium text-gray-500 pb-1 border-b">
                  <div className="col-span-8">Part Number</div>
                  <div className="col-span-4 text-right">Quantity</div>
                </div>
                {localParts.map((part, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-12 text-sm py-2 px-1 rounded-md ${
                      index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <div
                      className="col-span-8 font-medium text-gray-700 truncate"
                      title={part.part_number}
                    >
                      {part.part_number}
                    </div>
                    <div className="col-span-4 text-right text-gray-700">
                      {part.qty ? part.qty.toLocaleString() : 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                <p>No parts information available</p>
              </div>
            )}
            {showAddForm && (
              <div className="mt-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Add New Part</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Part Number</label>
                    <input
                      type="text"
                      value={newPartName}
                      onChange={(e) => {
                        setNewPartName(e.target.value);
                        setPartNameError(null);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter part number"
                    />
                    {partNameError && (
                      <p className="mt-1 text-xs text-red-600">{partNameError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                    <input
                      type="number"
                      value={newPartQuantity}
                      onChange={(e) => setNewPartQuantity(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter quantity"
                      min="1"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <PopupFooter />
        </div>
      </div>
    );
  }

  // Anchored popup (if anchorRef and position are provided).
  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-[600px] max-h-[90vh] overflow-hidden transition-all duration-300"
      style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-medium text-gray-700">
          Parts Info {poNumber && <span className="text-red-600 ml-1">PO #{poNumber}</span>}
        </h3>
        <button
          onClick={handleClose}
          className="p-1 rounded-full hover:bg-gray-200 transition-colors"
        >
          <X size={16} className="text-gray-500" />
        </button>
      </div>
      <div className="p-3 overflow-y-auto max-h-[80vh]">
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            {error}
          </div>
        )}
        {Array.isArray(localParts) && localParts.length > 0 ? (
          <div className="space-y-2">
            <div className="grid grid-cols-12 text-xs font-medium text-gray-500 pb-1 border-b">
              <div className="col-span-8">Part Number</div>
              <div className="col-span-4 text-right">Quantity</div>
            </div>
            {localParts.map((part, index) => (
              <div
                key={index}
                className={`grid grid-cols-12 text-sm py-2 px-1 rounded-md ${
                  index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                }`}
              >
                <div
                  className="col-span-8 font-medium text-gray-700 truncate"
                  title={part.part_number}
                >
                  {part.part_number}
                </div>
                <div className="col-span-4 text-right text-gray-700">
                  {part.qty ? part.qty.toLocaleString() : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <p>No parts information available</p>
          </div>
        )}
        {showAddForm && (
          <div className="mt-4 p-3 border border-gray-200 rounded-md bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Add New Part</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Part Number</label>
                <input
                  type="text"
                  value={newPartName}
                  onChange={(e) => {
                    setNewPartName(e.target.value);
                    setPartNameError(null);
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter part number"
                />
                {partNameError && (
                  <p className="mt-1 text-xs text-red-600">{partNameError}</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                <input
                  type="number"
                  value={newPartQuantity}
                  onChange={(e) => setNewPartQuantity(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter quantity"
                  min="1"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <PopupFooter />
      <div className="p-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex justify-between">
        <span>Total Parts: {localParts.length}</span>
        <span>
          Total Quantity: {localParts.reduce((sum, p) => sum + (p.qty || 0), 0).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default PartsPopup;
